import { Route } from 'react-router';
import { ModuleGate } from '../../components/ModuleGate';
import { FinanceSettingsPage } from './FinanceSettingsPage';
import { NumberingPage } from './NumberingPage';
import { InvoicesPage } from './InvoicesPage';
import { InvoiceEditorPage } from './InvoiceEditorPage';
import { InvoiceDetailPage } from './InvoiceDetailPage';
import { AccountsPage } from './AccountsPage';
import { ChartPage } from './ChartPage';
import { ChartSetupPage } from './ChartSetupPage';
import { ReportPage } from './ReportPage';
import { PostingsPage } from './PostingsPage';
import { AccountCreatePage } from './AccountCreatePage';
import { AccountDetailPage } from './AccountDetailPage';
import { PurchaseInvoicesPage } from './PurchaseInvoicesPage';
import { PurchaseInvoicePage } from './PurchaseInvoicePage';
import type { BackofficeModule } from '../registry';

/** Bausteine für andere Seiten: Finanzdaten in der Kontaktkarte, Rechnungen am Auftrag */
export { ContactFinance, ContactFinanceFactBox, ContactFinanceGlance } from './ContactFinance';
export { ContactAccountButtons } from './ContactAccountButtons';
export { OrderInvoices } from './OrderInvoices';

/**
 * Einstiegspunkt der Finanzen im Backoffice (siehe docs/finanzen.md). Er trägt die Einrichtung → Finanzen (KON-139), die für beide Rechnungsbereiche gilt, und die
 * Seiten der Ausgangsrechnungen (KON-151, Schlüssel `invoices_out`). Die Eingangsrechnungen (`invoices_in`) bekommen später ein eigenes Modul.
 */
export const financeModule: BackofficeModule = {
  key: 'invoices_out',
  path: '/rechnungen',
  // Debitoren (Kunden mit Personenkonto) haben eine eigene Liste und Maske neben den Rechnungen
  nav: [
    { label: 'Ausgangsrechnungen', path: '/rechnungen' },
    { label: 'Debitoren', path: '/debitoren', permission: 'contacts.finance', group: 'master', after: 'documents' },
  ],
  setupSections: [
    {
      path: 'finanzen',
      label: 'Finanzen',
      module: 'setup',
      permission: 'setup.read',
      requires: ['invoices_out', 'invoices_in'],
      description: 'Versteuerungsart, Kontenrahmen und DATEV, Zahlungsziel und Skonto, Konten je Steuerschlüssel, E-Rechnung und Bankkonten.',
      built: true,
      group: 'finance',
      keywords: ['Steuer', 'Umsatzsteuer', 'Kleinunternehmer', 'Sollversteuerung', 'Istversteuerung', 'DATEV', 'SKR03', 'SKR04', 'Kontenrahmen', 'Skonto', 'Zahlungsziel', 'Bank', 'IBAN', 'E-Rechnung', 'ZUGFeRD', 'Rechnung', 'Aufbewahrung'],
    },
    {
      path: 'rechnungsnummern',
      label: 'Rechnungsnummern',
      module: 'setup',
      permission: 'setup.read',
      requires: ['invoices_out'],
      description: 'Wie die Nummern Ihrer Rechnungen, Gutschriften und Stornos aufgebaut sind: mit Jahr, mit Mandantennummer oder nur als Nummernserie, und ab welcher Nummer sie beginnt.',
      built: true,
      group: 'finance',
      keywords: ['Rechnungsnummer', 'Nummernkreis', 'Nummernserie', 'Präfix', 'Kundennummer', 'Mandantennummer', 'Startwert', 'Gutschrift', 'Storno', 'GoBD', 'fortlaufend'],
    },
  ],
  setupRoutes: (
    <>
      <Route path="finanzen" element={<ModuleGate module="setup" permission="setup.read"><FinanceSettingsPage /></ModuleGate>} />
      <Route path="rechnungsnummern" element={<ModuleGate module="setup" permission="setup.read"><NumberingPage /></ModuleGate>} />
    </>
  ),
  routes: (
    <>
      <Route path="rechnungen" element={<ModuleGate module="invoices_out" permission="invoices_out.read"><InvoicesPage /></ModuleGate>} />
      <Route path="rechnungen/neu" element={<ModuleGate module="invoices_out" permission="invoices_out.write"><InvoiceEditorPage /></ModuleGate>} />
      <Route path="rechnungen/:id" element={<ModuleGate module="invoices_out" permission="invoices_out.read"><InvoiceDetailPage /></ModuleGate>} />
      <Route path="debitoren" element={<ModuleGate module="invoices_out" permission="contacts.finance"><AccountsPage kind="debtor" /></ModuleGate>} />
      <Route path="debitoren/neu" element={<ModuleGate module="invoices_out" permission="contacts.finance"><AccountCreatePage kind="debtor" /></ModuleGate>} />
      <Route path="debitoren/:id" element={<ModuleGate module="invoices_out" permission="contacts.finance"><AccountDetailPage kind="debtor" /></ModuleGate>} />
      <Route path="rechnungen/:id/bearbeiten" element={<ModuleGate module="invoices_out" permission="invoices_out.write"><InvoiceEditorPage /></ModuleGate>} />
    </>
  ),
};

/**
 * Einstiegspunkt der Eingangsrechnungen im Backoffice (KON-142, Schlüssel `invoices_in`). Er nutzt die Einrichtung → Finanzen und die Zahlungskarte der Ausgangsrechnungen
 * mit, trägt aber eigene Seiten: Liste, die Maske „Eingangsrechnung erstellen“ mit Import und die Ansicht mit Beleg, Freigabe und Zahlungen.
 */
export const purchaseModule: BackofficeModule = {
  key: 'invoices_in',
  path: '/eingangsrechnungen',
  // Kreditoren (Lieferanten mit Personenkonto) haben eine eigene Liste und Maske neben den Eingangsrechnungen
  nav: [
    { label: 'Eingangsrechnungen', path: '/eingangsrechnungen' },
    { label: 'Kreditoren', path: '/kreditoren', permission: 'contacts.finance', group: 'master', after: 'documents' },
  ],
  routes: (
    <>
      <Route path="eingangsrechnungen" element={<ModuleGate module="invoices_in" permission="invoices_in.read"><PurchaseInvoicesPage /></ModuleGate>} />
      <Route path="eingangsrechnungen/neu" element={<ModuleGate module="invoices_in" permission="invoices_in.write"><PurchaseInvoicePage /></ModuleGate>} />
      <Route path="kreditoren" element={<ModuleGate module="invoices_in" permission="contacts.finance"><AccountsPage kind="creditor" /></ModuleGate>} />
      <Route path="kreditoren/neu" element={<ModuleGate module="invoices_in" permission="contacts.finance"><AccountCreatePage kind="creditor" /></ModuleGate>} />
      <Route path="kreditoren/:id" element={<ModuleGate module="invoices_in" permission="contacts.finance"><AccountDetailPage kind="creditor" /></ModuleGate>} />
      <Route path="eingangsrechnungen/:id" element={<ModuleGate module="invoices_in" permission="invoices_in.read"><PurchaseInvoicePage /></ModuleGate>} />
    </>
  ),
};

/**
 * Einstiegspunkt des Kontenplans im Backoffice (KON-162, Schlüssel `chart`): der Plan mit Kategorien und Konten und der Finanzreport unter Finanzen und in
 * der Einrichtung der Wechsel des Kontenrahmens.
 */
export const chartModule: BackofficeModule = {
  key: 'chart',
  path: '/kontenplan',
  nav: [
    // Reihenfolge im Menü Finanzen: Sachposten, Kontenplan, dann Inventar und Anlagenspiegel (aus dem Modul Lager), der Finanzreport folgt dem Anlagenspiegel
    { label: 'Sachposten', path: '/sachposten', permission: 'chart.report' },
    { label: 'Kontenplan', path: '/kontenplan' },
    { label: 'Finanzreport', path: '/finanzreport', permission: 'chart.report', afterPath: ['/inventar/anlagenspiegel', '/inventar'] },
  ],
  setupSections: [
    {
      path: 'kontenplan',
      label: 'Kontenplan',
      module: 'setup',
      permission: 'setup.read',
      requires: 'chart',
      description: 'Kontenrahmen wählen und wechseln: Wirkia Standard für kleine Einzelunternehmen, SKR03 oder SKR04. Den Plan selbst pflegen Sie im Kontenplan.',
      built: true,
      group: 'finance',
      keywords: ['Kontenrahmen', 'SKR03', 'SKR04', 'Konten', 'Kategorien', 'DATEV', 'Steuerberater', 'Wechsel', 'Einzelunternehmen'],
    },
  ],
  setupRoutes: <Route path="kontenplan" element={<ModuleGate module="setup" permission="setup.read"><ChartSetupPage /></ModuleGate>} />,
  routes: (
    <>
      <Route path="kontenplan" element={<ModuleGate module="chart" permission="chart.read"><ChartPage /></ModuleGate>} />
      <Route path="finanzreport" element={<ModuleGate module="chart" permission="chart.report"><ReportPage /></ModuleGate>} />
      <Route path="sachposten" element={<ModuleGate module="chart" permission="chart.report"><PostingsPage /></ModuleGate>} />
    </>
  ),
};
